import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  DatePicker,
  Space,
  Divider,
  Typography,
  InputNumber,
  notification,
  Card,
  Tag,
  Modal
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { salesService } from '../../services/salesService';
import { companyService } from '../../services/companyService';
import { customerService } from '../../services/customerService';
import { itemService } from '../../services/itemService';
import { orderService } from '../../services/orderService';
import { numberToWords } from '../../utils/numberToWords';

const NATURE_OPTIONS = [
  {
    key: 'supply',
    label: 'Supply of Solar Power Generating System (70% of Total Project Value)',
    percentOfTotal: 70,
    cgst: 2.5,
    sgst: 2.5,
    descTemplate: "Supply of {KW}KW Solar Power Generating System (70% of Total Project Value)"
  },
  {
    key: 'installation',
    label: 'Installation of Solar Power Generating System (30% of Total Project Value)',
    percentOfTotal: 30,
    cgst: 9,
    sgst: 9,
    descTemplate: "Installation of Solar Power Generating System (30% of Total Project Value)"
  },
  {
    key: 'mseb',
    label: 'MSEB liasoning',
    percentOfTotal: 100, // Special logic
    cgst: 9,
    sgst: 9,
    descTemplate: "MSEB liasoning"
  },
  {
    key: 'other',
    label: 'Other / Custom Item',
    percentOfTotal: 0,
    cgst: 9,
    sgst: 9,
    descTemplate: ""
  }
];

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const SalesForm = ({ onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);      // Product link items (for optional linkage if needed, though user asked to remove "link to product", keeping just in case or can remove)
  const [orders, setOrders] = useState([]);    // Orders for selected customer
  const [selectedOrder, setSelectedOrder] = useState(null); // Full order object

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  useEffect(() => {
    fetchInitialData();
    // Set default date
    form.setFieldsValue({
      invoiceDate: dayjs(),
      items: [{
        description: '',
        quantity: 1,
        rate: 0,
        gstRate: 18,
        cgstRate: 9,
        sgstRate: 9,
        igstRate: 0
      }]
    });
    calculateTotal();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [compRes, custRes, itemRes] = await Promise.all([
        companyService.getAllCompanies(),
        customerService.getAllCustomers(),
        itemService.getAllItems()
      ]);
      setCompanies(compRes.data || compRes || []);
      setCustomers(custRes.data || custRes || []);
      setItems(itemRes.data || itemRes || []);
    } catch (error) {
      console.error('Failed to fetch initial data', error);
      notification.error({ message: 'Failed to load form data' });
    }
  };

  const handleCustomerChange = async (customerId) => {
    const cust = customers.find(c => c.id === customerId);
    setSelectedCustomer(cust);

    // Reset order selection
    setOrders([]);
    setSelectedOrder(null);
    form.setFieldsValue({ items: [] }); // Start fresh

    if (customerId) {
      try {
        const res = await orderService.getAllOrders({ customerId });
        // Robust check for different response structures
        const ordersData = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        // Only show closed orders as per user requirement (cannot invoice incomplete orders)
        const closedOrders = ordersData.filter(o => o.projectStatus === 'Closed');
        setOrders(closedOrders);
      } catch (error) {
        console.error("Failed to fetch orders", error);
        notification.error({ message: 'Failed to load customer orders' });
      }
    }
  };

  const handleOrderChange = async (orderId) => {
    if (!orderId) {
      setSelectedOrder(null);
      return;
    }

    try {
      setLoading(true);
      const res = await orderService.getOrderById(orderId);
      const order = res.data?.data || res.data || res;
      setSelectedOrder(order);

      if (order) {
        // Auto-fill values from order
        const mappedItems = (order.items || []).map(item => ({
          natureKey: item.natureKey || 'other',
          description: item.description || getDescriptionForNature(item.natureKey, order.plantSizeKw),
          quantity: item.quantity || 1,
          rate: item.rate || 0,
          cgstRate: item.cgstRate || 0,
          sgstRate: item.sgstRate || 0,
          igstRate: item.igstRate || 0,
          gstRate: item.gstRate || (item.cgstRate + item.sgstRate + item.igstRate) || 0,
          percentOfTotal: item.percentOfTotal || 0,
          itemId: item.itemId || null
        }));

        form.setFieldsValue({
          contractAmount: order.orderValue || 0,
          poNumber: order.contractReference || "",
          consumerNo: order.selectedConsumerNumber || "",
          items: mappedItems
        });

        // Trigger recalculation immediately with latest values
        calculateTotal({ ...order, contractAmount: order.orderValue || 0, items: mappedItems });
      }
    } catch (error) {
      console.error("Failed to fetch order details", error);
      notification.error({ message: 'Failed to load order details for auto-fill' });
    } finally {
      setLoading(false);
    }
  };

  const getDescriptionForNature = (key, plantSizeKw) => {
    const opt = NATURE_OPTIONS.find(n => n.key === key);
    if (!opt) return "";
    let desc = opt.descTemplate;
    const kw = plantSizeKw || "___";
    return desc.replace("{KW}", kw);
  };

  const handleNatureChange = (key, index) => {
    const opt = NATURE_OPTIONS.find(n => n.key === key);
    if (!opt) return;

    const contractAmount = form.getFieldValue('contractAmount') || 0;
    const plantSizeKw = selectedOrder?.plantSizeKw;

    let rate = 0;
    if (key === 'other') {
      rate = contractAmount / 1.18;
    } else if (key === 'mseb') {
      // User Rule: MSEB formula changes to 18% (1.18 divisor)
      rate = contractAmount / 1.18;
    } else {
      // User Rule: Supply/Install use 1.089 divisor
      const base = contractAmount / 1.089;
      rate = base * (opt.percentOfTotal / 100);
    }

    const currentItems = form.getFieldValue('items');
    const updatedItems = [...currentItems];
    updatedItems[index] = {
      ...updatedItems[index],
      natureKey: key,
      description: key === 'other' ? '' : getDescriptionForNature(key, plantSizeKw),
      quantity: 1,
      rate: Math.round(rate),
      cgstRate: opt.cgst,
      sgstRate: opt.sgst,
      igstRate: 0,
      gstRate: opt.cgst + opt.sgst,
      percentOfTotal: opt.percentOfTotal
    };
    form.setFieldsValue({ items: updatedItems });
    calculateTotal();
  };

  const calculateTotal = (customValues = null) => {
    const formValues = customValues || form.getFieldsValue(true);
    const items = formValues.items || [];
    const contractAmount = Math.round(parseFloat(formValues.contractAmount || 0));

    let totalBeforeTax = 0;
    let totalTax = 0;

    items.forEach(item => {
      if (!item) return;
      const qty = parseFloat(item.quantity || 0);
      const rate = parseFloat(item.rate || 0);
      const cgst = parseFloat(item.cgstRate || 0);
      const sgst = parseFloat(item.sgstRate || 0);
      const igst = parseFloat(item.igstRate || 0);

      const amount = qty * rate;
      const itemTax = amount * ((cgst + sgst + igst) / 100);

      totalBeforeTax += amount;
      totalTax += itemTax;
    });

    const roundedBeforeTax = Math.round(totalBeforeTax);
    const roundedTax = Math.round(totalTax);
    const computedGrandTotal = Math.round(totalBeforeTax + totalTax);

    // Rounding adjustment: If the difference between items total and contract total is <= 5,
    // we match the contract amount to ensure exact reconciliation.
    const diff = contractAmount > 0 ? (contractAmount - computedGrandTotal) : 0;
    const roundedGrandTotal = (contractAmount > 0 && Math.abs(diff) <= 5) ? contractAmount : computedGrandTotal;
    const roundOff = (contractAmount > 0 && Math.abs(diff) <= 5) ? diff : 0;

    form.setFieldsValue({
      totalBeforeTax: roundedBeforeTax,
      totalTax: roundedTax,
      totalAfterTax: roundedGrandTotal,
      roundOff,
      amountInWords: numberToWords(roundedGrandTotal)
    });
  };

  const handleCompanyChange = async (companyId) => {
    try {
      const result = await salesService.getNextInvoiceNumber(companyId);
      if (result.success) {
        form.setFieldsValue({ invoiceNumber: result.data });
      }
    } catch (error) {
      console.error("Failed to fetch next invoice number");
    }
  };

  const handleSubmit = () => {
    const allValues = form.getFieldsValue(true);
    setPendingValues(allValues);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingValues) return;
    const values = pendingValues;

    try {
      setLoading(true);
      setIsConfirmModalOpen(false);
      const data = {
        companyId: values.companyId,
        customerId: values.customerId,
        orderId: values.orderId,
        invoiceDate: values.invoiceDate.toISOString(),
        invoiceNumber: values.invoiceNumber,
        poNumber: values.poNumber,
        consumerNo: values.consumerNo,
        contractAmount: parseFloat(values.contractAmount || 0),
        totalBeforeTax: parseFloat(values.totalBeforeTax || 0),
        totalTax: parseFloat(values.totalTax || 0),
        totalAfterTax: parseFloat(values.totalAfterTax || 0),
        roundOff: parseFloat(values.roundOff || 0),
        amountInWords: values.amountInWords,
        items: values.items.map(item => {
          const cgst = parseFloat(item.cgstRate || 0);
          const sgst = parseFloat(item.sgstRate || 0);
          const igst = parseFloat(item.igstRate || 0);
          const combinedGst = cgst + sgst + igst;

          const payload = {
            itemId: item.itemId ? parseInt(item.itemId) : null,
            description: item.description || "",
            natureKey: item.natureKey || "",
            quantity: parseFloat(item.quantity || 1),
            rate: parseFloat(item.rate || 0),
            gstRate: combinedGst,
            cgstRate: cgst,
            sgstRate: sgst,
            igstRate: igst,
            percentOfTotal: item.percentOfTotal
          };
          return payload;
        })
      };

      await salesService.createSalesInvoice(data);
      notification.success({
        message: 'Success',
        description: 'Sales invoice created successfully'
      });
      onSuccess();
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.error || error.message || 'Failed to create invoice'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onValuesChange={(changed, all) => {
        if (changed.contractAmount !== undefined) {
          const currentItems = all.items || [];
          const updatedItems = currentItems.map(item => {
            if (item && item.natureKey && item.natureKey !== 'other') {
              const opt = NATURE_OPTIONS.find(n => n.key === item.natureKey);
              if (opt) {
                const contractAmount = changed.contractAmount || 0;
                let rate = 0;
                if (item.natureKey === 'mseb') {
                  rate = contractAmount / 1.18;
                } else {
                  rate = (contractAmount / 1.089) * (opt.percentOfTotal / 100);
                }
                return { ...item, rate: Math.round(rate) };
              }
            }
            return item;
          });
          form.setFieldsValue({ items: updatedItems });
          // Must call with current ALL values including the updated items
          calculateTotal({ ...all, items: updatedItems });
        } else {
          calculateTotal(all);
        }
      }}
      style={{ padding: '0' }}
    >
      <div style={{ background: '#f0f2f5', padding: '10px', borderRadius: '8px' }}>
        <Card size="small" style={{ marginBottom: 16, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>New Sales Invoice</Title>
              <Text type="secondary">Generate invoice as per contract agreement</Text>
            </div>
            <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>DRAFT</Tag>
          </div>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label={<Text strong>Issued By (Supplier)</Text>}
                name="companyId"
                rules={[{ required: true, message: 'Select Issuer' }]}
              >
                <Select placeholder="Select Supplier" size="large" onChange={handleCompanyChange} style={{ width: '100%' }}>
                  {companies.map(c => (
                    <Option key={c.id} value={c.id}>{c.companyName}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<Text strong>Billed To (Customer)</Text>}
                name="customerId"
                rules={[{ required: true, message: 'Select Customer' }]}
              >
                <Select
                  placeholder="Select Customer"
                  size="large"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleCustomerChange}
                >
                  {customers.map(c => (
                    <Option key={c.id} value={c.id}>{c.customerName}</Option>
                  ))}
                </Select>
              </Form.Item>
              {selectedCustomer && (
                <>
                  <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: '8px', padding: '16px', marginTop: '-8px', marginBottom: '16px' }}>
                    <Row gutter={16}>
                      <Col span={16}>
                        <Text type="secondary" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Billing Address</Text>
                        <div style={{ marginTop: 4, fontSize: '13px', lineHeight: '1.5' }}>
                          {selectedCustomer.customerAddress || 'Address details not provided'}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>GST Details</Text>
                        <div style={{ marginTop: 4 }}>
                          <Tag color={selectedCustomer.gstn ? "blue" : "default"}>
                            {selectedCustomer.gstn || 'Unregistered'}
                          </Tag>
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Consumer No.</Text>
                          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.consumerNo !== currentValues.consumerNo}>
                            {({ getFieldValue }) => (
                              <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{getFieldValue('consumerNo') || 'N/A'}</div>
                            )}
                          </Form.Item>
                          <Form.Item name="consumerNo" hidden><Input /></Form.Item>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {/* Order Select Logic */}
                  {console.log("Current orders in state:", orders)}
                  <Form.Item
                    label={<Text strong>Select Order / Contract</Text>}
                    name="orderId"
                    rules={[{ required: true, message: 'Please select an order' }]}
                  >
                    <Select
                      placeholder="Select Order"
                      size="large"
                      onChange={handleOrderChange}
                      allowClear
                      showSearch
                    >
                      {orders.map(o => (
                        <Option key={o.id} value={o.id}>
                          Order #{o.id} - {dayjs(o.orderDate).format('DD MMM YYYY')} {o.plantSizeKw ? `- ${o.plantSizeKw}KW` : ''}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {selectedOrder && (
                    <div style={{ padding: '8px 12px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '6px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>Linked Contract: </Text>
                      <Text strong>{selectedOrder.contractReference || 'N/A'}</Text>
                      <Divider type="vertical" />
                      <Text type="secondary" style={{ fontSize: '12px' }}>Plant Size: </Text>
                      <Text strong>{selectedOrder.plantSizeKw} KW</Text>
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }} />

          <Row gutter={24}>
            <Col span={5}>
              <Form.Item
                label={<Text strong>Invoice Date</Text>}
                name="invoiceDate"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: '100%' }} size="large" format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                label={<Text strong>Invoice Number</Text>}
                name="invoiceNumber"
                tooltip="Leave empty for auto-generation"
              >
                <Input placeholder="Auto-generated (e.g. #PP/...)" size="large" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                label={<Text strong>PO / Contract Ref</Text>}
                name="poNumber"
              >
                <Input placeholder="Ref #" size="large" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                label={<Text strong>Total Contract Value</Text>}
                name="contractAmount"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  prefix="₹"
                  size="large"
                  placeholder="0"
                  status="warning"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Items Section */}
        <Card
          size="small"
          title={
            <Space>
              <FileTextOutlined style={{ color: '#1890ff' }} />
              <Title level={5} style={{ margin: 0, fontSize: '15px' }}>Saleable Items & Services</Title>
            </Space>
          }
          bodyStyle={{ padding: '0' }}
          style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: 16 }}
        >
          <Form.List name="items">
            {(fields, { add, remove }) => {
              // Calculate total allocation to handle 100% constraint
              const items = form.getFieldValue('items') || [];
              const totalAlloc = items.reduce((sum, i) => sum + (i.percentOfTotal || 0), 0);
              const isFull = totalAlloc >= 100;

              return (
                <>
                  <div style={{
                    background: '#fafafa',
                    padding: '16px',
                    borderBottom: '1px solid #f0f0f0',
                    fontWeight: '600',
                    fontSize: '11px',
                    color: '#595959',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    <Row gutter={4}>
                      <Col span={9}>Nature of Supply</Col>
                      <Col span={2} style={{ textAlign: 'center' }}>CGST</Col>
                      <Col span={2} style={{ textAlign: 'center' }}>SGST</Col>
                      <Col span={2} style={{ textAlign: 'center' }}>IGST</Col>
                      <Col span={2} style={{ textAlign: 'center' }}>Alloc %</Col>
                      <Col span={2} style={{ textAlign: 'center' }}>Qty</Col>
                      <Col span={5} style={{ textAlign: 'center' }}>Rate</Col>
                    </Row>
                  </div>

                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} style={{
                      padding: '16px',
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa'
                    }}>
                      <Row gutter={4} align="top">
                        <Col span={9}>
                          <Form.Item
                            {...restField}
                            name={[name, 'natureKey']}
                            rules={[{ required: true, message: 'Required' }]}
                            style={{ marginBottom: 4 }}
                          >
                            <Select
                              placeholder="Select Nature"
                              onChange={(val) => handleNatureChange(val, index)}
                              size="middle"
                            >
                              {NATURE_OPTIONS.map(opt => (
                                <Option key={opt.key} value={opt.key}>{opt.label.split('(')[0].trim()}</Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item shouldUpdate={(prev, curr) => prev.items?.[index]?.natureKey !== curr.items?.[index]?.natureKey} noStyle>
                            {({ getFieldValue }) => {
                              const natureKey = getFieldValue(['items', index, 'natureKey']);
                              const description = getFieldValue(['items', index, 'description']);
                              return (
                                <>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'description']}
                                    rules={[{ required: true, message: 'Please enter custom item name', whitespace: true }]}
                                    style={{ marginBottom: 4 }}
                                    hidden={natureKey !== 'other'}
                                  >
                                    <Input placeholder="Enter custom item name" size="middle" />
                                  </Form.Item>
                                  {natureKey !== 'other' && (
                                    <div style={{ fontSize: '10px', color: '#8c8c8c', paddingLeft: 4, lineHeight: '1.2', marginTop: '-2px' }}>
                                      {description}
                                    </div>
                                  )}
                                </>
                              );
                            }}
                          </Form.Item>

                        </Col>
                        <Col span={2}>
                          <Form.Item {...restField} name={[name, 'cgstRate']} style={{ marginBottom: 0 }}>
                            <InputNumber disabled={items[index]?.natureKey !== 'other'} size="middle" min={0} max={28} style={{ width: '100%', fontSize: '12px' }} precision={2} placeholder="0%" />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Form.Item {...restField} name={[name, 'sgstRate']} style={{ marginBottom: 0 }}>
                            <InputNumber disabled={items[index]?.natureKey !== 'other'} size="middle" min={0} max={28} style={{ width: '100%', fontSize: '12px' }} precision={2} placeholder="0%" />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Form.Item {...restField} name={[name, 'igstRate']} style={{ marginBottom: 0 }}>
                            <InputNumber disabled={items[index]?.natureKey !== 'other'} size="middle" min={0} max={28} style={{ width: '100%', fontSize: '12px' }} precision={2} placeholder="0%" />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Form.Item {...restField} name={[name, 'percentOfTotal']} style={{ marginBottom: 0 }}>
                            <InputNumber disabled={items[index]?.natureKey !== 'other'} size="middle" min={0} max={100} style={{ width: '100%' }} suffix="%" precision={1} />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Form.Item {...restField} name={[name, 'quantity']} initialValue={1} style={{ marginBottom: 0 }}>
                            <InputNumber min={0.1} size="middle" style={{ width: '100%' }} placeholder="Qty" />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item {...restField} name={[name, 'rate']} style={{ marginBottom: 0 }} rules={[{ required: true }]}>
                            <InputNumber disabled={items[index]?.natureKey !== 'other'} size="middle" min={0} style={{ width: '100%' }} prefix="₹" placeholder="Rate" />
                          </Form.Item>
                        </Col>
                      </Row>


                      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const row = getFieldValue(['items', index]) || {};
                            const q = row.quantity || 0;
                            const r = row.rate || 0;
                            const cgst = row.cgstRate || 0;
                            const sgst = row.sgstRate || 0;
                            const igst = row.igstRate || 0;
                            const base = Math.round(q * r);
                            const tax = Math.round(base * ((cgst + sgst + igst) / 100));

                            return (
                              <Space size="large" style={{ padding: '4px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
                                <div>
                                  <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>TAXABLE VALUE</Text>
                                  <Text strong>₹ {base}</Text>
                                </div>
                                <div>
                                  <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>GST ({cgst + sgst + igst}%)</Text>
                                  <Text strong>₹ {tax}</Text>
                                </div>
                                <div>
                                  <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>ITEM TOTAL</Text>
                                  <Text strong style={{ color: '#52c41a' }}>₹ {base + tax}</Text>
                                </div>
                              </Space>
                            );
                          }}
                        </Form.Item>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                          style={{ borderRadius: '50%' }}
                        />
                      </div>
                    </div>
                  ))}

                  <div style={{ padding: '16px' }}>
                    {!isFull && <Button
                      type="dashed"
                      onClick={() => add({ quantity: 1, cgstRate: 0, sgstRate: 0, igstRate: 0, gstRate: 0 })}
                      block
                      icon={<PlusOutlined />}
                      style={{ height: '40px', borderRadius: '8px', fontSize: '14px' }}
                    >
                      Add Line Item
                    </Button>}
                    {isFull && <Tag color="green">Allocation Full (100%)</Tag>}
                  </div>
                </>
              );
            }}
          </Form.List>
        </Card>

        {/* Final Footer Section */}
        <Card size="small" style={{ marginTop: 0, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Row gutter={32}>
            <Col span={14}>
              <Form.Item label={<Text strong>Amount in Words</Text>} name="amountInWords" style={{ marginBottom: 0 }}>
                <TextArea rows={4} placeholder="Full amount text..." style={{ borderRadius: '8px', padding: '8px' }} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <div style={{ background: '#fafafa', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                <Row style={{ marginBottom: 16 }}>
                  <Col span={14}><Text type="secondary" style={{ fontSize: '13px' }}>TOTAL TAXABLE VALUE</Text></Col>
                  <Col span={10} style={{ textAlign: 'right' }}>
                    <Form.Item shouldUpdate noStyle>
                      {({ getFieldValue }) => (
                        <Text strong style={{ fontSize: '18px' }}>₹ {getFieldValue('totalBeforeTax') || 0}</Text>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row style={{ marginBottom: 16 }}>
                  <Col span={14}><Text type="secondary" style={{ fontSize: '13px' }}>TOTAL GST AMOUNT</Text></Col>
                  <Col span={10} style={{ textAlign: 'right' }}>
                    <Form.Item shouldUpdate noStyle>
                      {({ getFieldValue }) => (
                        <Text strong style={{ fontSize: '13px' }}>₹ {getFieldValue('totalTax') || 0}</Text>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Divider style={{ margin: '13px 0' }} />
                <Row align="middle">
                  <Col span={12}><Title level={3} style={{ margin: 0, color: '#1890ff' }}>GRAND TOTAL</Title></Col>
                  <Col span={12} style={{ textAlign: 'right' }}>
                    <Form.Item shouldUpdate noStyle>
                      {({ getFieldValue }) => (
                        <Title level={3} style={{ margin: 0, color: '#1890ff' }}>₹ {getFieldValue('totalAfterTax') || 0}</Title>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: 24 }}>
            <Button size="large" onClick={onCancel} style={{ width: '130px', height: '40px', borderRadius: '8px' }}>Cancel</Button>
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => {
                const totalAfterTax = getFieldValue('totalAfterTax') || 0;
                const contractAmount = getFieldValue('contractAmount') || 0;
                // Simple exact match might be risky with rounding, but user asked specifically.
                // We can use small epsilon if needed, but lets try strict first.
                const isValid = totalAfterTax === contractAmount && contractAmount > 0;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>

                    {!isValid && <Text type="danger" style={{ fontSize: '12px' }}>Total ({totalAfterTax}) must match Contract Value ({contractAmount})</Text>}

                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      size="large"
                      disabled={!isValid}
                      style={{
                        width: '210px',
                        height: '40px',
                        borderRadius: '8px',
                        fontWeight: '700',
                        letterSpacing: '0.5px',
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.25)'
                      }}
                    >
                      CREATE FINAL INVOICE
                    </Button>
                  </div>
                );
              }}
            </Form.Item>
          </div>
        </Card>
      </div>


      <Modal
        title={<Title level={4} style={{ margin: 0 }}>Confirm Invoice Creation</Title>}
        open={isConfirmModalOpen}
        onOk={handleConfirmSubmit}
        onCancel={() => setIsConfirmModalOpen(false)}
        okText="Yes, Create Invoice"
        cancelText="Review"
        okButtonProps={{ loading, size: 'large' }}
        cancelButtonProps={{ size: 'large' }}
        width={500}
        centered
      >
        <div style={{ padding: '16px 0' }}>
          <Text type="secondary">Please review the invoice summary before finalized creation:</Text>
          <Divider style={{ margin: '12px 0' }} />

          <Row gutter={[0, 12]}>
            <Col span={8}><Text type="secondary">Customer:</Text></Col>
            <Col span={16}><Text strong>{selectedCustomer?.customerName}</Text></Col>

            <Col span={8}><Text type="secondary">Invoice #:</Text></Col>
            <Col span={16}><Text strong>{pendingValues?.invoiceNumber || 'Auto'}</Text></Col>

            <Col span={8}><Text type="secondary">Date:</Text></Col>
            <Col span={16}><Text strong>{pendingValues?.invoiceDate?.format('DD MMM YYYY')}</Text></Col>

            <Col span={8}><Text type="secondary">Grand Total:</Text></Col>
            <Col span={16}>
              <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                ₹ {(pendingValues?.totalAfterTax || 0).toLocaleString()}
              </Title>
            </Col>
          </Row>

          <Divider style={{ margin: '12px 0' }} />
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', background: '#fffbe6', padding: '10px', borderRadius: '6px', border: '1px solid #ffe58f' }}>
            <FileTextOutlined style={{ color: '#faad14' }} />
            <Text style={{ fontSize: '12px' }}>This action will generate a permanent invoice record and can be exported to Excel for printing.</Text>
          </div>
        </div>
      </Modal>
    </Form>
  );
};

export default SalesForm;